import { useCallback, useEffect, useMemo, useState } from 'react';
import { normaliseTrades, type TradeRecord } from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import { fetchTradeRows } from './api.js';

export interface Transactions {
  readonly status: 'loading' | 'ready' | 'error';
  /** Settled movements, newest first. Empty until ready. */
  readonly records: readonly TradeRecord[];
  readonly retry: () => void;
}

/**
 * The account's transaction history from `get_trade` `type=ALL` (MYP-146), loaded by the Wallet
 * page when it opens. The rows are kept as fetched, so switching language relabels them without
 * asking the vendor again.
 */
export function useTransactions(): Transactions {
  const { locale } = useLocale();
  const language = locale === 'zh' ? 'zh' : 'en';
  const [rows, setRows] = useState<readonly unknown[]>([]);
  const [status, setStatus] = useState<Transactions['status']>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Only the newest attempt's answer lands (StrictMode's double mount, or a retry mid-flight).
    let current = true;
    setStatus('loading');
    fetchTradeRows().then(
      (next) => {
        if (!current) return;
        setRows(next);
        setStatus('ready');
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown: the vendor's reason is Chinese prose from its own code table.
        console.error('[wallet] could not load the transaction history', cause);
        setStatus('error');
      },
    );
    return () => {
      current = false;
    };
  }, [attempt]);

  const records = useMemo(() => normaliseTrades(rows, language), [rows, language]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { status, records, retry };
}
